"use client"

import { useState } from "react"
import { UserPlus, UserCheck } from "lucide-react"
import { Button } from "~/components/ui/button"
import { AuthPromptDialog } from "~/components/auth/AuthPromptDialog"
import { env } from "~/env"
import { trackActivity } from "~/lib/activity-tracking"

type FacilitatorFollowButtonProps = {
  facilitatorId: string
  initialFollowing?: boolean
  accessToken?: string
  isAuthenticated?: boolean
  variant?: "icon" | "button"
  /**
   * `gold` is the public profile hero's treatment (#1019, Steve's 2026-09-02
   * design): a gold pill while not following, a cream outline once following.
   * The hero sits on photography, where salmon fails contrast (#946 FR-7), so
   * this tone never uses it. Everywhere else keeps the default salmon.
   */
  tone?: "salmon" | "gold"
  onToggle?: (facilitatorId: string, following: boolean) => void
}

const GOLD_PILL_CLASS =
  "inline-flex items-center gap-1.5 rounded-full px-6 py-[13px] text-[15px] font-bold transition-shadow font-[family-name:var(--font-rounded)]"

export function FacilitatorFollowButton({
  facilitatorId,
  initialFollowing = false,
  accessToken,
  isAuthenticated,
  variant = "button",
  tone = "salmon",
  onToggle,
}: FacilitatorFollowButtonProps) {
  const [following, setFollowing] = useState(initialFollowing)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isAuthenticated === false) {
      setShowAuthPrompt(true)
      return
    }

    const newState = !following
    setFollowing(newState)

    try {
      const res = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/facilitator-followers/${facilitatorId}`,
        {
          method: newState ? "POST" : "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          ...(newState
            ? { body: JSON.stringify({ source: "MANUAL_FOLLOW" }) }
            : {}),
        },
      )
      if (!res.ok) {
        setFollowing(!newState) // revert on error
      } else {
        onToggle?.(facilitatorId, newState)
        if (newState) {
          trackActivity('FACILITATOR_FOLLOWED', 'FACILITATOR', facilitatorId, { accessToken, skipDedup: true })
        }
      }
    } catch {
      setFollowing(!newState)
    }
  }

  if (variant === "icon") {
    return (
      <>
        <button
          onClick={handleToggle}
          title={following ? "Unfollow" : "Follow"}
          className="p-2 rounded-full hover:bg-neutral-100 transition"
        >
          {following ? (
            <UserCheck className="h-5 w-5 text-[#DE866D] transition" />
          ) : (
            <UserPlus className="h-5 w-5 text-neutral-600 transition" />
          )}
        </button>
        <AuthPromptDialog
          open={showAuthPrompt}
          onOpenChange={setShowAuthPrompt}
          title="Follow facilitators"
          description="Create your free account to follow facilitators and get updates about their events."
          icon="👤"
        />
      </>
    )
  }

  if (tone === "gold") {
    return (
      <>
        <button
          type="button"
          onClick={handleToggle}
          data-testid="facilitator-follow-gold"
          className={`${GOLD_PILL_CLASS} ${
            following
              ? "hover:bg-[rgba(251,244,230,.22)]"
              : "hover:shadow-[0_6px_16px_rgba(217,158,55,.4)]"
          }`}
          style={
            following
              ? {
                  background: "rgba(251,244,230,.12)",
                  color: "#FBF4E6",
                  boxShadow: "inset 0 0 0 1.5px rgba(251,244,230,.6)",
                }
              : {
                  background: "#F0B853",
                  color: "#2C2D2B",
                  boxShadow: "inset 0 0 0 1.5px #D99E37",
                }
          }
        >
          {following ? (
            <>
              <UserCheck className="h-4 w-4" />
              Following
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4" />
              Follow
            </>
          )}
        </button>
        <AuthPromptDialog
          open={showAuthPrompt}
          onOpenChange={setShowAuthPrompt}
          title="Follow facilitators"
          description="Create your free account to follow facilitators and get updates about their events."
          icon="👤"
        />
      </>
    )
  }

  return (
    <>
      <Button
        onClick={handleToggle}
        variant={following ? "outline" : "default"}
        size="sm"
        className={
          following
            ? "border-[#DE866D] text-[#DE866D] hover:bg-[#DE866D]/5"
            : "bg-[#DE866D] hover:bg-[#C97257] text-white"
        }
      >
        {following ? (
          <>
            <UserCheck className="h-4 w-4 mr-1.5" />
            Following
          </>
        ) : (
          <>
            <UserPlus className="h-4 w-4 mr-1.5" />
            Follow
          </>
        )}
      </Button>
      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="Follow facilitators"
        description="Create your free account to follow facilitators and get updates about their events."
        icon="👤"
      />
    </>
  )
}
